import { useState, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import { colorTokens } from "../../theme/tokens";

export interface ExplorerLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
}

/**
 * The sidebar's width, also used by AppShell to offset the role tab so it
 * starts at the main pane's left edge (files-explorer-reference.png),
 * and the breakpoint below which the sidebar becomes a drawer, exported
 * so AppShell can align its own responsive behaviour to the same point.
 */
export const SIDEBAR_WIDTH = 400;
export const SIDEBAR_BREAKPOINT = "md";

/**
 * The explorer's two-pane shell (files-explorer-reference.png): a ~400px
 * light-grey sidebar and a flexible main pane. The sidebar becomes a
 * drawer below the "md" breakpoint (task requirement).
 */
export function ExplorerLayout({ sidebar, children }: ExplorerLayoutProps) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down(SIDEBAR_BREAKPOINT));
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Box sx={{ display: "flex", flexGrow: 1, minHeight: 0 }}>
      {isSmall ? (
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
          <Box
            sx={{ width: SIDEBAR_WIDTH, maxWidth: "80vw", bgcolor: colorTokens.bg, height: "100%" }}
            onClick={() => setMobileOpen(false)}
          >
            {sidebar}
          </Box>
        </Drawer>
      ) : (
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            bgcolor: colorTokens.bg,
            borderRight: "1px solid",
            borderColor: "divider",
            overflowY: "auto",
          }}
        >
          {sidebar}
        </Box>
      )}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          // White main pane (files-explorer-reference.png): only the
          // sidebar above is the light grey `colorTokens.bg`.
          bgcolor: colorTokens.surface,
        }}
      >
        {isSmall && (
          <Box sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
            <IconButton onClick={() => setMobileOpen(true)} aria-label="Open folders">
              <MenuIcon />
            </IconButton>
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
}
